import { Input, Select, Switch } from '@components'
import { CreateDocumentAttachmentsSection } from './CreateDocumentAttachmentsSection'
import { ContactSelector } from '@components/uas/ContactSelector'
import { useBusinessContext } from '@context'
import { fetchAccountingRule, IAccountingRule } from '@query'
import BalanceCheckBar from '@root/pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/DocumentCreation/BalanceCheckBar'
import EntriesSelector from '@root/pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/DocumentCreation/EntriesSelector'
import { setAPIErrors, type APIError } from '@root/utils'
import { useQuery } from '@tanstack/react-query'
import { useEdges, useNodes } from '@xyflow/react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
  type FieldNamesMarkedBoolean,
  type UseFormReturn
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  CreateDocumentDateConfig,
  CreateDocumentDateRule,
  CreateDocumentNodeConfig
} from '../../nodes/types'
import { useNodeEditor } from '../NodeEditorContext'
import { TabButton, TabGroup } from '../segmentedTabs.styles'
import { resolveIncomingCreateDocumentKind } from './incomingCreateDocumentKind'
import {
  applyCreateDocumentRuleClientErrors,
  CreateDocumentRuleFieldSource,
  CreateDocumentRuleFormValues,
  defaultCreateDocumentRuleValues,
  valuesFromAccountingRule,
  CreateDocumentRuleSaveError
} from './saveCreateDocumentRule'

type FieldSource = CreateDocumentRuleFieldSource
type DateSource = CreateDocumentDateConfig['source']

export type CreateDocumentFormValues = CreateDocumentRuleFormValues & {
  date: CreateDocumentDateConfig
  amount: number | null
  amountSource: FieldSource
  as_draft: boolean
  copy_attachments: boolean
}

const PRIMARY_DATE_RULES: CreateDocumentDateRule[] = [
  'run',
  'plusDays',
  'plusMonthsLastDay',
  'plusMonthsFirstDay'
]

const LEGACY_DATE_RULES: CreateDocumentDateRule[] = [
  'monthEnd',
  'monthStart',
  'prevMonthEnd',
  'nextMonthStart'
]

const isPlusMonthsRule = (
  rule: CreateDocumentDateRule
): rule is 'plusMonthsLastDay' | 'plusMonthsFirstDay' =>
  rule === 'plusMonthsLastDay' || rule === 'plusMonthsFirstDay'

const dateRulesForSelect = (current: CreateDocumentDateRule): CreateDocumentDateRule[] =>
  LEGACY_DATE_RULES.includes(current) && !PRIMARY_DATE_RULES.includes(current)
    ? [...PRIMARY_DATE_RULES, current]
    : PRIMARY_DATE_RULES

const RULE_DIRTY_KEYS = [
  'description',
  'descriptionSource',
  'contactSource',
  'contactId',
  'blueprint',
  'attachment_exempt'
] as const

const incomingDate = (): CreateDocumentDateConfig => ({ source: 'incoming' })

const COMPLETE_EUROS = /^-?\d+(?:[.,]\d+)?$/

const readDate = (config: CreateDocumentNodeConfig): CreateDocumentDateConfig => {
  const value = config.date
  if (value && typeof value === 'object' && 'source' in value) {
    return value
  }
  return incomingDate()
}

const readAmount = (config: CreateDocumentNodeConfig): number | null => {
  const value = config.amount
  if (typeof value === 'number' && value !== 0) return value
  return null
}

const formatCentsDraft = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',')

const parseEurosToCents = (raw: string): number | null => {
  const normalized = raw.trim().replaceAll(',', '.')
  if (!normalized || !COMPLETE_EUROS.test(normalized)) return null
  const euros = Number(normalized)
  if (!Number.isFinite(euros)) return null
  const cents = Math.round(euros * 100)
  return cents === 0 ? null : cents
}

const toRuleValues = (values: CreateDocumentFormValues): CreateDocumentRuleFormValues => ({
  description: values.description,
  descriptionSource: values.descriptionSource,
  contactSource: values.contactSource,
  contactId: values.contactId,
  blueprint: values.blueprint,
  attachment_exempt: values.attachment_exempt
})

const isRuleFieldsDirty = (
  dirtyFields: FieldNamesMarkedBoolean<CreateDocumentFormValues>
): boolean => RULE_DIRTY_KEYS.some(key => Boolean(dirtyFields[key]))

const adjustRuleForIncomingKind = (
  values: CreateDocumentRuleFormValues,
  incomingKind: string
): CreateDocumentRuleFormValues => {
  if (incomingKind !== 'none') return values
  return {
    ...values,
    descriptionSource: 'own',
    contactSource: typeof values.contactId === 'number' ? 'own' : 'incoming'
  }
}

interface SourceOption<T extends string> {
  value: T
  label: string
}

interface SourceToggleProps<T extends string> {
  value: T
  options: SourceOption<T>[]
  ariaLabel: string
  onChange: (value: T) => void
}

function SourceToggle<T extends string>({
  value,
  options,
  ariaLabel,
  onChange
}: SourceToggleProps<T>) {
  return (
    <SourceTabGroup role="radiogroup" aria-label={ariaLabel}>
      {options.map(option => (
        <SourceTab
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          $selected={value === option.value}
          onClick={() => {
            if (value !== option.value) {
              onChange(option.value)
            }
          }}
        >
          {option.label}
        </SourceTab>
      ))}
    </SourceTabGroup>
  )
}

interface Props {
  nodeId: string
  config: CreateDocumentNodeConfig
  onChange: (config: CreateDocumentNodeConfig) => void
}

export const CreateDocumentConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const {
    configSnapshot,
    getPendingCreateDocumentRule,
    upsertPendingCreateDocumentRule,
    clearPendingCreateDocumentRule,
    registerCreateDocumentRuleSession
  } = useNodeEditor()
  const nodes = useNodes()
  const edges = useEdges()
  const incomingKind = useMemo(
    () => resolveIncomingCreateDocumentKind(nodeId, nodes, edges),
    [edges, nodeId, nodes]
  )
  const isClaim = incomingKind === 'bank_transaction'
  const canUseIncoming = incomingKind === 'document'

  const ruleId = config.accounting_rule_id
  const ruleQuery = useQuery({
    queryKey: [slug, 'accounting-rule', ruleId],
    queryFn: () => fetchAccountingRule({ slug, id: ruleId as number }),
    enabled: ruleId != null
  })

  const pendingRule = getPendingCreateDocumentRule(nodeId)

  const loadedRuleValues = useMemo(
    () =>
      adjustRuleForIncomingKind(
        pendingRule?.values ??
          (ruleQuery.data
            ? valuesFromAccountingRule(ruleQuery.data)
            : defaultCreateDocumentRuleValues()),
        incomingKind
      ),
    [incomingKind, pendingRule?.values, ruleQuery.data]
  )

  const savedBaseline = useMemo((): CreateDocumentFormValues => {
    const snap = (configSnapshot ?? {}) as unknown as CreateDocumentNodeConfig
    const amount = readAmount(snap)
    return {
      ...loadedRuleValues,
      date: readDate(snap),
      amount,
      amountSource: amount != null ? 'own' : 'incoming',
      as_draft: Boolean(snap.as_draft),
      copy_attachments: snap.copy_attachments !== false
    }
  }, [configSnapshot, loadedRuleValues])

  const methods = useForm<CreateDocumentFormValues>({
    values: savedBaseline,
    resetOptions: { keepDirtyValues: true },
    mode: 'onTouched'
  })

  const {
    control,
    register,
    setValue,
    getValues,
    formState: { isDirty, dirtyFields, errors }
  } = methods

  const initialAmount = readAmount((configSnapshot ?? config) as CreateDocumentNodeConfig)
  const [amountDraft, setAmountDraft] = useState(() =>
    initialAmount != null ? formatCentsDraft(initialAmount) : ''
  )
  const [showStandaloneAmountError, setShowStandaloneAmountError] = useState(false)

  const pushNodeSlice = (partial?: {
    date?: CreateDocumentDateConfig
    amount?: number | null
    as_draft?: boolean
    copy_attachments?: boolean
  }) => {
    const values = getValues()
    onChange({
      accounting_rule_id: config.accounting_rule_id ?? null,
      date: partial?.date ?? values.date,
      amount: partial && 'amount' in partial ? partial.amount! : values.amount,
      as_draft: partial?.as_draft ?? values.as_draft,
      copy_attachments: partial?.copy_attachments ?? values.copy_attachments
    })
  }

  const syncAmountDraftFromCents = (cents: number | null) => {
    setAmountDraft(cents != null ? formatCentsDraft(cents) : '')
  }

  const descriptionSource = useWatch({ control, name: 'descriptionSource' })
  const contactSource = useWatch({ control, name: 'contactSource' })
  const amountSource = useWatch({ control, name: 'amountSource' })
  const copyAttachments = useWatch({ control, name: 'copy_attachments' })
  const nodeAmount = useWatch({ control, name: 'amount' })

  const amountOk = incomingKind !== 'none' || (typeof nodeAmount === 'number' && nodeAmount !== 0)
  const standaloneAmountError =
    incomingKind === 'none' && showStandaloneAmountError && !amountOk
      ? { type: 'required' as const, message: t('validation.required') }
      : undefined

  const methodsRef = useRef(methods)
  methodsRef.current = methods
  const dirtyFieldsRef = useRef(dirtyFields)
  dirtyFieldsRef.current = dirtyFields
  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty

  useEffect(() => {
    const requiredMessage = t('validation.required')
    const readRuleDraft = (): CreateDocumentRuleFormValues =>
      toRuleValues(methodsRef.current.getValues())

    const getDraft = () => ({
      nodeId,
      ruleId: ruleId ?? null,
      values: structuredClone(readRuleDraft())
    })
    const isRuleDirty = () => isRuleFieldsDirty(dirtyFieldsRef.current)
    const onRevert = () => {
      methodsRef.current.reset()
      clearPendingCreateDocumentRule(nodeId)
      const amount = methodsRef.current.getValues('amount')
      setAmountDraft(typeof amount === 'number' && amount !== 0 ? formatCentsDraft(amount) : '')
      setShowStandaloneAmountError(false)
    }
    const onSaveBlocked = (error: CreateDocumentRuleSaveError) => {
      setShowStandaloneAmountError(true)
      applyCreateDocumentRuleClientErrors(
        readRuleDraft(),
        methodsRef.current.setError,
        requiredMessage
      )
      void methodsRef.current.trigger()
      if (error.httpError?.status === 400 && error.httpError.data) {
        setAPIErrors(error.httpError.data as APIError, methodsRef.current.setError)
      }
    }
    const onPersisted = () => {
      methodsRef.current.reset(methodsRef.current.getValues())
      setShowStandaloneAmountError(false)
    }

    if (getPendingCreateDocumentRule(nodeId)?.showErrors) {
      setShowStandaloneAmountError(true)
      applyCreateDocumentRuleClientErrors(
        readRuleDraft(),
        methodsRef.current.setError,
        requiredMessage
      )
      void methodsRef.current.trigger()
    }

    registerCreateDocumentRuleSession({
      nodeId,
      getDraft,
      isDirty: isRuleDirty,
      isFormDirty: isDirtyRef.current,
      onRevert,
      onSaveBlocked,
      onPersisted
    })
  }, [
    clearPendingCreateDocumentRule,
    getPendingCreateDocumentRule,
    isDirty,
    nodeId,
    registerCreateDocumentRuleSession,
    ruleId,
    t
  ])

  const clearPendingRef = useRef(clearPendingCreateDocumentRule)
  clearPendingRef.current = clearPendingCreateDocumentRule
  const upsertPendingRef = useRef(upsertPendingCreateDocumentRule)
  upsertPendingRef.current = upsertPendingCreateDocumentRule
  const getPendingRef = useRef(getPendingCreateDocumentRule)
  getPendingRef.current = getPendingCreateDocumentRule
  const registerSessionRef = useRef(registerCreateDocumentRuleSession)
  registerSessionRef.current = registerCreateDocumentRuleSession
  const ruleIdRef = useRef(ruleId)
  ruleIdRef.current = ruleId

  useEffect(() => {
    return () => {
      const values = toRuleValues(methodsRef.current.getValues())
      const hadPending = Boolean(getPendingRef.current(nodeId))
      if (isRuleFieldsDirty(dirtyFieldsRef.current) || hadPending) {
        upsertPendingRef.current({
          nodeId,
          ruleId: ruleIdRef.current ?? null,
          values: structuredClone(values)
        })
      } else {
        clearPendingRef.current(nodeId)
      }
      registerSessionRef.current(null)
    }
  }, [nodeId])

  const incomingOwnOptions: SourceOption<FieldSource>[] = [
    { value: 'incoming', label: t('workflows.config.createDocument.fromIncoming') },
    { value: 'own', label: t('workflows.config.createDocument.setOwn') }
  ]
  const amountIsOwn = incomingKind === 'none' || amountSource === 'own'
  const showFieldSourceToggle = incomingKind !== 'none'
  const descriptionIsOwn = incomingKind === 'none' || descriptionSource === 'own'
  const contactIsOwn = incomingKind === 'none' || contactSource === 'own'
  const contactRequired = incomingKind !== 'none' && contactSource === 'own'

  return (
    <FormProvider {...(methods as unknown as UseFormReturn<IAccountingRule>)}>
      <Form onSubmit={event => event.preventDefault()}>
        <FieldsScroll>
          <Intro>
            {t(
              isClaim
                ? 'workflows.nodes.createDocument.descriptionClaim'
                : 'workflows.nodes.createDocument.descriptionCreate'
            )}
          </Intro>

          {!isClaim && (
            <Section>
              <Controller
                name="date"
                control={control}
                render={({ field }) => {
                  const stored = field.value ?? incomingDate()
                  const dateValue =
                    !canUseIncoming && stored.source === 'incoming'
                      ? { source: 'calc' as const, rule: 'run' as const }
                      : stored
                  const writeDate = (next: CreateDocumentDateConfig) => {
                    field.onChange(next)
                    pushNodeSlice({ date: next })
                  }
                  const currentMonths =
                    dateValue.source === 'calc' &&
                    (dateValue.rule === 'plusMonthsLastDay' ||
                      dateValue.rule === 'plusMonthsFirstDay')
                      ? dateValue.months
                      : 1
                  const monthsRule =
                    dateValue.source === 'calc' &&
                    (dateValue.rule === 'plusMonthsLastDay' ||
                      dateValue.rule === 'plusMonthsFirstDay')
                      ? dateValue.rule
                      : null
                  return (
                    <>
                      <SectionHeader>
                        <SectionTitle>{t('workflows.config.createDocument.date')}</SectionTitle>
                        {canUseIncoming && (
                          <SourceToggle
                            ariaLabel={t('workflows.config.createDocument.date')}
                            value={dateValue.source === 'calc' ? 'calc' : 'incoming'}
                            options={[
                              {
                                value: 'incoming',
                                label: t('workflows.config.createDocument.fromIncoming')
                              },
                              {
                                value: 'calc',
                                label: t('workflows.config.createDocument.calculated')
                              }
                            ]}
                            onChange={(source: DateSource) => {
                              if (source === 'incoming') {
                                writeDate(incomingDate())
                                return
                              }
                              writeDate({ source: 'calc', rule: 'run' })
                            }}
                          />
                        )}
                      </SectionHeader>
                      {dateValue.source === 'calc' && (
                        <DateCalcRow>
                          <Select
                            id={`workflow-create-document-date-rule-${nodeId}`}
                            name={`workflow-create-document-date-rule-${nodeId}`}
                            label={t('workflows.config.createDocument.dateRule')}
                            value={dateValue.rule}
                            onChange={event => {
                              const rule = event.target.value as CreateDocumentDateRule
                              if (rule === 'plusDays') {
                                writeDate({
                                  source: 'calc',
                                  rule: 'plusDays',
                                  days: dateValue.rule === 'plusDays' ? dateValue.days : 14
                                })
                                return
                              }
                              if (isPlusMonthsRule(rule)) {
                                writeDate({
                                  source: 'calc',
                                  rule,
                                  months: currentMonths
                                })
                                return
                              }
                              writeDate({ source: 'calc', rule })
                            }}
                          >
                            {dateRulesForSelect(dateValue.rule).map(rule => (
                              <option key={rule} value={rule}>
                                {t(`workflows.config.createDocument.dateRules.${rule}`)}
                              </option>
                            ))}
                          </Select>
                          {dateValue.rule === 'plusDays' && (
                            <DaysField>
                              <Input
                                id={`workflow-create-document-date-days-${nodeId}`}
                                name={`workflow-create-document-date-days-${nodeId}`}
                                type="number"
                                min={1}
                                step={1}
                                label={t('workflows.config.createDocument.days')}
                                value={String(dateValue.days)}
                                onChange={event => {
                                  const parsed = Number(event.target.value)
                                  if (!Number.isInteger(parsed) || parsed < 1) return
                                  writeDate({ source: 'calc', rule: 'plusDays', days: parsed })
                                }}
                                autoComplete="off"
                              />
                            </DaysField>
                          )}
                          {monthsRule && (
                            <DaysField>
                              <Input
                                id={`workflow-create-document-date-months-${nodeId}`}
                                name={`workflow-create-document-date-months-${nodeId}`}
                                type="number"
                                min={0}
                                step={1}
                                label={t('workflows.config.createDocument.months')}
                                value={String(currentMonths)}
                                onChange={event => {
                                  const parsed = Number(event.target.value)
                                  if (!Number.isInteger(parsed) || parsed < 0) return
                                  writeDate({
                                    source: 'calc',
                                    rule: monthsRule,
                                    months: parsed
                                  })
                                }}
                                autoComplete="off"
                              />
                            </DaysField>
                          )}
                        </DateCalcRow>
                      )}
                    </>
                  )
                }}
              />
            </Section>
          )}

          {!isClaim && (
            <Section>
              <SectionHeader>
                <SectionTitle>{t('workflows.config.createDocument.amount')}</SectionTitle>
                {canUseIncoming && (
                  <Controller
                    name="amountSource"
                    control={control}
                    render={({ field }) => (
                      <SourceToggle
                        ariaLabel={t('workflows.config.createDocument.amount')}
                        value={field.value}
                        options={incomingOwnOptions}
                        onChange={(source: FieldSource) => {
                          field.onChange(source)
                          if (source === 'incoming') {
                            setAmountDraft('')
                            setValue('amount', null, { shouldDirty: true })
                            pushNodeSlice({ amount: null })
                            return
                          }
                          const currentAmount = getValues('amount')
                          if (typeof currentAmount === 'number' && currentAmount !== 0) {
                            setAmountDraft(formatCentsDraft(currentAmount))
                          }
                        }}
                      />
                    )}
                  />
                )}
              </SectionHeader>
              {amountIsOwn && (
                <Input
                  id={`workflow-create-document-amount-${nodeId}`}
                  name={`workflow-create-document-amount-${nodeId}`}
                  label={t('workflows.config.createDocument.amount')}
                  placeholder={t('workflows.config.createDocument.amountPlaceholder')}
                  value={amountDraft}
                  required={incomingKind === 'none'}
                  errors={standaloneAmountError}
                  onChange={event => {
                    const nextDraft = event.target.value
                    setAmountDraft(nextDraft)
                    if (amountSource !== 'own') {
                      setValue('amountSource', 'own', { shouldDirty: true })
                    }
                    if (!nextDraft.trim()) {
                      setValue('amount', null, { shouldDirty: true })
                      pushNodeSlice({ amount: null })
                      return
                    }
                    const cents = parseEurosToCents(nextDraft)
                    if (cents != null) {
                      setValue('amount', cents, { shouldDirty: true })
                      pushNodeSlice({ amount: cents })
                    }
                  }}
                  onBlur={() => {
                    if (incomingKind === 'none') setShowStandaloneAmountError(true)
                    const currentAmount = getValues('amount')
                    if (typeof currentAmount === 'number' && currentAmount !== 0) {
                      syncAmountDraftFromCents(currentAmount)
                      return
                    }
                    if (parseEurosToCents(amountDraft) == null) {
                      setAmountDraft('')
                      setValue('amount', null, { shouldDirty: true })
                      pushNodeSlice({ amount: null })
                    }
                  }}
                  autoComplete="off"
                  inputMode="decimal"
                />
              )}
              <Hint>
                {amountIsOwn
                  ? t('workflows.config.createDocument.amountOwnHint')
                  : t('workflows.config.createDocument.amountIncomingHint')}
              </Hint>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <SectionTitle>{t('workflows.config.createDocument.description')}</SectionTitle>
              {showFieldSourceToggle && (
                <Controller
                  name="descriptionSource"
                  control={control}
                  render={({ field }) => (
                    <SourceToggle
                      ariaLabel={t('workflows.config.createDocument.description')}
                      value={field.value}
                      options={incomingOwnOptions}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </SectionHeader>
            {descriptionIsOwn && (
              <Input
                label={t('workflows.config.createDocument.description')}
                placeholder={t('workflows.config.createDocument.descriptionPlaceholder')}
                autoComplete="off"
                required
                errors={errors.description}
                {...register('description', {
                  validate: (value, formValues) =>
                    (incomingKind !== 'none' && formValues.descriptionSource !== 'own') ||
                    Boolean(value?.trim()) ||
                    t('validation.required')
                })}
              />
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>{t('workflows.config.createDocument.contact')}</SectionTitle>
              {showFieldSourceToggle && (
                <Controller
                  name="contactSource"
                  control={control}
                  render={({ field }) => (
                    <SourceToggle
                      ariaLabel={t('workflows.config.createDocument.contact')}
                      value={field.value}
                      options={incomingOwnOptions}
                      onChange={field.onChange}
                    />
                  )}
                />
              )}
            </SectionHeader>
            {contactIsOwn && (
              <Controller
                control={control}
                name="contactId"
                rules={{
                  validate: value =>
                    !contactRequired || typeof value === 'number' || t('validation.required')
                }}
                render={({ field, fieldState: { error } }) => (
                  <ContactSelector
                    {...field}
                    onChange={value => {
                      field.onChange(value)
                      if (incomingKind === 'none') {
                        setValue('contactSource', typeof value === 'number' ? 'own' : 'incoming', {
                          shouldDirty: true
                        })
                      }
                    }}
                    errors={error}
                    ref={null}
                    required={contactRequired}
                    fetchOnlyInvoicableContacts={false}
                    label={t('workflows.config.createDocument.contact')}
                    noDataMessage={t('contacts.contactSelector.noDataDocumentForm')}
                  />
                )}
              />
            )}
          </Section>

          <CreateDocumentAttachmentsSection
            nodeId={nodeId}
            canUseIncoming={canUseIncoming}
            control={control as never}
            setValue={setValue as never}
            copyAttachments={copyAttachments}
            onCopyAttachmentsChange={copy => pushNodeSlice({ copy_attachments: copy })}
          />

          {!isClaim && (
            <Section>
              <SectionHeader>
                <SectionTitle>{t('workflows.config.createDocument.asDraft')}</SectionTitle>
                <Controller
                  name="as_draft"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id={`workflow-create-document-as-draft-${nodeId}`}
                      checked={Boolean(field.value)}
                      onChange={event => {
                        const checked = event.target.checked
                        field.onChange(checked)
                        pushNodeSlice({ as_draft: checked })
                      }}
                      height={24}
                    />
                  )}
                />
              </SectionHeader>
              <Hint>{t('workflows.config.createDocument.asDraftHint')}</Hint>
            </Section>
          )}

          <EntriesSection>
            <SectionTitle>{t('workflows.config.createDocument.entries')}</SectionTitle>
            {ruleQuery.isLoading && ruleId != null ? (
              <Hint>{t('general.loading')}</Hint>
            ) : (
              <>
                <EntriesSelector
                  baseName="blueprint.debet_entries"
                  header={t('settings.automation.accountingRules.form.document.debet')}
                  buttonLabel={t('settings.automation.accountingRules.form.document.addDebitRow')}
                />
                <EntriesSelector
                  baseName="blueprint.credit_entries"
                  header={t('settings.automation.accountingRules.form.document.credit')}
                  buttonLabel={t('settings.automation.accountingRules.form.document.addCreditRow')}
                />
                <BalanceCheckBar />
              </>
            )}
          </EntriesSection>
        </FieldsScroll>
      </Form>
    </FormProvider>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
`

const FieldsScroll = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`

const Intro = styled.p`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.md}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
  line-height: 1.5;
  text-wrap: pretty;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  min-width: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const EntriesSection = styled(Section)`
  gap: ${({ theme }) => theme.spacing.md}rem;

  > * {
    min-width: 0;
  }
`

const SectionTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const SourceTabGroup = styled(TabGroup)`
  max-width: 100%;
`

const SourceTab = styled(TabButton)`
  flex: 1;
  white-space: nowrap;
  line-height: 1.25;
  text-align: center;
`

const DateCalcRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;
`

const DaysField = styled.div`
  width: 9rem;
  max-width: 100%;
  flex-shrink: 0;

  .input-wrapper label > span {
    overflow: visible;
    text-overflow: clip;
  }
`

const Hint = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`
